'use client';

import { Box, Button } from '@/primitives';
import { blue } from '@/lib/colours';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useRouter } from 'next/navigation';
import { PUBLIC_ROUTES } from '@/configs/routes';
import { ErrorCowCard } from '@/components/utils/ErrorCowCard';

interface ErrorBoundaryFallbackProps {
  error?: Error;
  resetError?: () => void;
}

export function ErrorBoundaryFallback({
  error,
  resetError,
}: ErrorBoundaryFallbackProps) {
  const router = useRouter();

  const handleRefresh = () => {
    if (resetError) {
      resetError();
    } else {
      window.location.reload();
    }
  };

  const handleGoHome = () => {
    router.push(PUBLIC_ROUTES.HOME);
  };

  return (
    <ErrorCowCard
      title="Oops! Something went wrong"
      description={
        <>
          We encountered an unexpected error. Don&apos;t worry - we&apos;ve
          logged this issue and one of our developers will take a look.
          {process.env.NODE_ENV === 'development' && error && (
            <Box
              sx={{
                mt: 2,
                padding: '16px',
                bgcolor: 'action.hover',
                borderRadius: '8px',
                border: '1px solid',
                borderColor: 'divider',
                textAlign: 'left',
              }}
            >
              <Box
                component="code"
                sx={{
                  color: 'text.secondary',
                  fontFamily: 'monospace',
                  wordBreak: 'break-word',
                  fontSize: '12px',
                }}
              >
                {error.message}
              </Box>
            </Box>
          )}
        </>
      }
      actions={
        <Box
          sx={{
            display: 'flex',
            gap: '12px',
            justifyContent: 'center',
            flexWrap: 'wrap',
          }}
        >
          <Button
            variant="contained"
            startIcon={<RefreshIcon />}
            onClick={handleRefresh}
            sx={{
              backgroundColor: blue[600],
              '&:hover': { backgroundColor: blue[700] },
            }}
          >
            Try Again
          </Button>
          <Button
            variant="outlined"
            onClick={handleGoHome}
            sx={{
              borderColor: 'divider',
              color: 'text.primary',
              '&:hover': {
                borderColor: 'text.secondary',
                bgcolor: 'action.hover',
              },
            }}
          >
            Go to Home
          </Button>
        </Box>
      }
    />
  );
}
